import { useEffect, useRef, useState } from 'react';
import { Outlet, useNavigate, useLocation, Link } from 'react-router-dom';
import { useSelector, useDispatch } from 'react-redux';
import { RootState, AppDispatch } from '../store';
import { hydrateSession } from '../store/auth-actions';
import { fetchUnreadCount } from '../store/messages-actions';
import { authActions } from '../store/auth';
import { userActions } from '../store/user';
import { API_ENDPOINTS } from '../config/api';
import { usePageTracking } from '../hooks/usePageTracking';
import { setBadgeCount } from '../utils/appBadge';
import { registerServiceWorker } from '../utils/pushSubscribe';

import classes from './Root.module.scss';
import Notifications from '../components/Notifications';
import BottomNav from '../components/BottomNav';
import TopBarActions from '../components/TopBarActions';
import HomeLogo from '../components/HomeLogo';
import PublicAuthLinks from '../components/PublicAuthLinks';
import ContactModal from '../components/ContactModal';
import ContractsChoiceModal from '../components/ContractsChoiceModal';
import AddToHomeScreen from '../components/AddToHomeScreen';
import { BottomNavProvider } from '../context/BottomNavContext';
import { CONTRACTS_AI_ENABLED } from '../config/features';

// The marketing family Root renders through its Outlet: the homepage, its
// region-scoped local-SEO variants and the service pages. Each one renders
// its own header, so Root's floating Sign-in pill would duplicate the child
// header's Sign in, and none of them get the app shell or BottomNav.
//
// One list, used by every check below. It used to be copied three times
// with "keep these in sync" comments, and they drifted the moment a new
// service page (/grounds) was added.
const MARKETING_ROUTES = new Set<string>([
  '/',
  '/locations',
  '/central-coast',
  '/newcastle',
  '/sydney',
  '/cleaning',
  '/building-management',
  '/grounds-and-gardens',
  // The old service paths. They only ever redirect, but they have to be
  // in this list or the redirect hop renders inside the app shell.
  '/caretaking',
  '/grounds',
  '/maintenance',
  '/compliance',
  '/platform',
  // Audience landing pages — same marketing family as the service pages.
  '/strata-managers',
  '/owners-corporations',
  '/short-stay-accommodation',
]);

// Paths the app itself serves. Anything not on this list and not in the
// marketing family is treated as a user feed (/@handle and bare user
// ids), which is also what lets a signed-out visitor reach one. A new
// app route missing from here is therefore classified as someone's feed
// and never redirected to /login — which is how /contracts came to be in
// one copy of this list and not the other.
//
// One list, like MARKETING_ROUTES above, because there were two copies of
// it in this file and they had already diverged.
const KNOWN_ROUTES = [
  '/messages', '/building/', '/post/', '/admin', '/crm', '/tag/',
  '/terms', '/privacy', '/login', '/my', '/notifications', '/contracts',
];

// Routes that opt out of the "no default building yet" redirect to /my.
// /contracts has its own building picker; /crm is Strand's own sales
// pipeline and has nothing to do with the viewer owning a lot. Without
// this, staff with no property of their own get bounced to /my the
// moment they open the pipeline.
//
// /my itself is deliberately not on this list: it is the redirect's
// destination, and it must still mark the check as done so a
// buildingless user who has already seen the add-property CTA isn't
// dragged back to it from every other page.
const BUILDINGLESS_ROUTES = ['/contracts', '/crm'];

const isKnownRoute = (pathname: string): boolean =>
  KNOWN_ROUTES.some(r => pathname.startsWith(r));

const isBuildinglessRoute = (pathname: string): boolean =>
  BUILDINGLESS_ROUTES.some(r => pathname === r || pathname.startsWith(`${r}/`));

const Root = (): JSX.Element | null => {
  const dispatch: AppDispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const user = useSelector((state: RootState) => state.user.user);
  const isAuthenticated = useSelector((state: RootState) => state.auth.isAuthenticated);
  const authUserId = useSelector((state: RootState) => state.auth.userId);
  const authUserEmail = useSelector((state: RootState) => state.auth.email);
  const isAdmin = useSelector((state: RootState) => state.auth.isAdmin);
  const unreadMessages = useSelector((state: RootState) => state.messages.unreadCount);
  const hasCheckedBuildingRef = useRef(false);
  const [showContactModal, setShowContactModal] = useState(false);
  const [showContractsChoice, setShowContractsChoice] = useState(false);
  // Track page views with Google Analytics
  usePageTracking();

  // Disable automatic scroll restoration
  useEffect(() => {
    window.history.scrollRestoration = 'manual';
  }, []);

  // Reset scroll to the top on every route change so navigating between
  // building sub-pages (amenities → contacts → info → …) always lands at
  // the top instead of preserving the previous page's scroll offset. Skip
  // when the URL carries a hash so BuildingDetail's in-page anchor jumps
  // (e.g. #details) still work.
  useEffect(() => {
    if (location.hash) return;
    window.scrollTo(0, 0);
  }, [location.pathname]);

  // On boot: if we don't have a client-side session (Safari's ITP wipes
  // localStorage after 7 days), try to hydrate from the httpOnly cookie.
  // Server-set cookies survive that eviction so the user stays signed in.
  useEffect(() => {
    if (!isAuthenticated) {
      dispatch(hydrateSession());
    }
    // Cross-tab sync: when auth changes in another tab, mirror it here so all
    // Safari tabs/windows share the login state instantly (no reload needed).
    const onStorage = (e: StorageEvent) => {
      if (e.key !== 'auth') return;
      if (e.newValue) {
        try {
          const parsed = JSON.parse(e.newValue);
          if (parsed?.jwt && parsed?.userId) {
            dispatch(authActions.setAuthenticated({
              email: parsed.email,
              token: parsed.token ?? '',
              jwt: parsed.jwt,
              userId: parsed.userId,
              isAdmin: parsed.isAdmin === true,
              role: parsed.role ?? 'user',
            }));
          }
        } catch { /* ignore */ }
      } else {
        // Another tab logged out — mirror it.
        dispatch(authActions.logout());
        dispatch(userActions.clearUser());
      }
    };
    window.addEventListener('storage', onStorage);
    return () => window.removeEventListener('storage', onStorage);
  }, [dispatch, isAuthenticated]);

  // Sync user from auth state
  useEffect(() => {
    if (isAuthenticated && authUserId && user.id !== authUserId) {
      // Load user data from auth
      fetch(API_ENDPOINTS.users.getUser(authUserId))
        .then(res => res.json())
        .then(data => {
          if (data.user) {
            dispatch(userActions.setUser(data.user));
            // Keep auth role/admin flag in sync with live DB values
            dispatch(authActions.syncRoleFromUser({
              isAdmin: data.user.is_admin === true,
              role: data.user.role ?? 'user',
            }));
          }
        })
        .catch(err => console.error('Failed to load user:', err));
    }
  }, [isAuthenticated, authUserId, user.id, dispatch]);

  useEffect(() => {
    // Allow unauthenticated access to building detail pages, the landing page, and user feeds
    const isBuildingDetailPage = location.pathname.startsWith('/building/');
    const isPostDetailPage = location.pathname.startsWith('/post/');
    const isTagPage = location.pathname.startsWith('/tag/');
    const isLandingPage = MARKETING_ROUTES.has(location.pathname);
    const isUserFeedPage = location.pathname.startsWith('/@') ||
      (!isKnownRoute(location.pathname) && !isLandingPage);

    // First check authentication (skip for public pages)
    if (!isAuthenticated && !isBuildingDetailPage && !isPostDetailPage && !isTagPage && !isLandingPage && !isUserFeedPage) {
      navigate('/login');
      return;
    }

    // Redirect authenticated users from the landing family (root + region
    // variants) to the My Properties dashboard. /my is now also the "add a
    // property" entry point (empty-state CTA opens the address-search modal),
    // so we no longer need a separate /onboarding.
    if (isAuthenticated && isLandingPage) {
      navigate('/my');
      return;
    }

    // Users without a building land on /my — the empty-state CTA there opens
    // the address-search modal directly, which used to live at /onboarding.
    // /contracts and /crm are exempt — see BUILDINGLESS_ROUTES above.
    const isBuildinglessPage = isBuildinglessRoute(location.pathname);
    if (isAuthenticated && !user.building && !isBuildingDetailPage && !isPostDetailPage && !isTagPage && !isUserFeedPage && !isBuildinglessPage && !hasCheckedBuildingRef.current) {
      hasCheckedBuildingRef.current = true;
      if (location.pathname !== '/my') navigate('/my');
    } else if (user.building) {
      // Mark as checked if user has a building
      hasCheckedBuildingRef.current = true;
    }
  }, [isAuthenticated, user.building, user.id, location.pathname, navigate]);

  // Centralised unread message polling — single source, prevents multiple badge instances each polling.
  // Also re-fetch when the app returns to the foreground (mobile suspends timers while backgrounded).
  useEffect(() => {
    if (!isAuthenticated) return;
    dispatch(fetchUnreadCount());
    const interval = setInterval(() => dispatch(fetchUnreadCount()), 30000);
    const onVisible = () => {
      if (document.visibilityState === 'visible') dispatch(fetchUnreadCount());
    };
    document.addEventListener('visibilitychange', onVisible);
    return () => {
      clearInterval(interval);
      document.removeEventListener('visibilitychange', onVisible);
    };
  }, [isAuthenticated, dispatch]);

  // Mirror unread messages onto the installed app icon (PWA badge).
  // Socket events update the Redux count in real time, so this stays live.
  useEffect(() => {
    if (!isAuthenticated) return;
    setBadgeCount('messages', unreadMessages);
  }, [isAuthenticated, unreadMessages]);

  // Register the push service worker only when authenticated. Registering it on
  // /login has previously caused transient "Failed to fetch" errors on the first
  // sign-in request. If the user is not authenticated, unregister any lingering
  // registration from earlier sessions so /login is never SW-controlled.
  useEffect(() => {
    if (!('serviceWorker' in navigator)) return;
    if (isAuthenticated) {
      registerServiceWorker();
      return;
    }
    navigator.serviceWorker.getRegistrations()
      .then((regs) => Promise.all(regs.map((r) => r.unregister())))
      .catch(() => { /* best-effort */ });
  }, [isAuthenticated]);

  // Allow unauthenticated access to building detail pages, the landing page, and user feeds
  const isBuildingDetailPage = location.pathname.startsWith('/building/');
  const isPostDetailPage = location.pathname.startsWith('/post/');
  const isTagPage = location.pathname.startsWith('/tag/');
  const isLandingPage = MARKETING_ROUTES.has(location.pathname);
  const isUserFeedPage = location.pathname.startsWith('/@') ||
    (!isKnownRoute(location.pathname) && !isLandingPage);

  // Unauthenticated on the landing page — render just the outlet (no app shell).
  // Landing has no BottomNav so the banner sits flush with the bottom edge.
  if (!isAuthenticated && isLandingPage) {
    return (
      <>
        <Outlet />
        <AddToHomeScreen />
      </>
    );
  }

  // If not authenticated and not viewing a public page, show nothing while redirecting
  if (!isAuthenticated && !isBuildingDetailPage && !isPostDetailPage && !isTagPage && !isUserFeedPage) {
    return null;
  }

  // If the user has no building yet, /my hosts the add-property CTA — the
  // guard used to hide the shell while redirecting to /onboarding; we now
  // just let /my render so the empty-state pushes the modal.
  if (isAuthenticated && !user.building && location.pathname !== '/my' && !isBuildinglessRoute(location.pathname) && !isBuildingDetailPage && !isPostDetailPage && !isTagPage && !isUserFeedPage && !hasCheckedBuildingRef.current) {
    return null;
  }

  // Show the app-level nav only when authenticated. The BottomNav
  // component itself decides what to render — for public visitors on
  // building detail pages it renders the contextual pill row with
  // non-Property tabs greyed out, so we always mount it.

  const handleMessageUs = (e: React.MouseEvent<HTMLAnchorElement>) => {
    e.preventDefault();
    setShowContactModal(true);
  };

  return (
    <BottomNavProvider>
      <div className={classes.layout}>
        <Notifications />
        <HomeLogo />
        {isAuthenticated ? (
          <TopBarActions />
        ) : (
          // Every signed-out visitor gets the same header cluster — phone,
          // Sign up, Sign in. This used to be a lone "Sign in" pill here
          // while the marketing pages showed the full set, so a building
          // page looked like a different site.
          //
          // Landing and ServicePage render outside this shell (see
          // MARKETING_ROUTES) and mount PublicAuthLinks themselves, so
          // the suppression list stays as a guard against both appearing
          // if one of those routes is ever brought inside the shell.
          MARKETING_ROUTES.has(location.pathname) ? null : <PublicAuthLinks />
        )}
        <BottomNav />
        <div className={classes.layout__main}>
          <div className={`${classes.layout__content} ${location.pathname === '/messages' ? classes['layout__content--messages'] : ''}`}>
            <Outlet></Outlet>
          </div>
          <footer className={classes.layout__footer}>
            <p className={classes.layout__footerCta}>
              {isAuthenticated && CONTRACTS_AI_ENABLED && (
                <a
                  href="#"
                  onClick={(e) => { e.preventDefault(); setShowContractsChoice(true); }}
                >
                  Contracts engine
                </a>
              )}
            </p>
            <p>
              {isAuthenticated && (
                <>
                  <a href="#" onClick={handleMessageUs}>
                    Report a bug
                  </a>
                  {' • '}
                </>
              )}
              <Link to="/about">About</Link>
              {' • '}
              <Link to="/terms">Terms</Link>
              {' • '}
              <Link to="/privacy">Privacy</Link>
            </p>
            <p>
              <a href="tel:+61494017172">+61 494 017 172</a>
            </p>
          </footer>
        </div>
          {showContactModal && (
            <ContactModal
              onClose={() => setShowContactModal(false)}
              userEmail={authUserEmail || ''}
              userName={user.name || ''}
              title="Report a bug"
            />
          )}
          {showContractsChoice && CONTRACTS_AI_ENABLED && (() => {
            const b = user.building;
            const slug = b?.id
              ? (b.addressSlug || (b.plusCode ? b.plusCode.replace(/\+/g, '-') : null) || b.id)
              : null;
            return (
              <ContractsChoiceModal
                hasPublicBuilding={!!b?.id}
                askHref={slug ? `/building/${slug}/ask?focus=1` : undefined}
                onClose={() => setShowContractsChoice(false)}
                onPickPublic={() => {
                  setShowContractsChoice(false);
                  if (!slug) return;
                  navigate(`/building/${slug}/documents`);
                }}
                onPickPersonal={() => {
                  setShowContractsChoice(false);
                  navigate('/contracts');
                }}
              />
            );
          })()}
        <AddToHomeScreen variant="floating" />
      </div>
    </BottomNavProvider>
  );
};

export default Root;
